import {
  Engine,
  EvaluationResultSpecific,
  Policy,
} from '@agilelab/plugin-wb-governance-common';
import {
  WbCard,
  WbCardContent,
  WbTabsContent,
  WbTabsList,
  WbTabsListContext,
  WbTabsTrigger,
} from '@agilelab/plugin-wb-platform';
import { Box } from '@material-ui/core';
import { FC, useMemo, useState } from 'react';
import { PolicyCueScriptEditor } from '../../../PolicyDetail';
import { PolicyWittyGovernanceAgentEditor } from '../../../PolicyDetail/PolicyWittyGovernanceAgentEditor';
import { GovernanceTestOverviewInfo } from '../GovernanceTestOverviewInfo';
import { GovernanceTestOverviewPolicyDetails } from './PolicyViolationsDetails';
import { GovernanceTestOverviewPolicyInfo } from './OverviewPolicyInfoBox';

interface Props {
  result?: EvaluationResultSpecific;
  policy: Policy;
  infoContent?: JSX.Element;
  test?: {
    errors?: string[];
    content: string;
    name: string;
  };
}

enum Tab {
  Result = 'result',
  Entity = 'policy',
}

export const GovernanceTestOverviewPolicyTabs: FC<Props> = props => {
  const [selectedTab, setSelectedTab] = useState<Tab>(Tab.Result);

  const test = useMemo(() => {
    if (props.test) return props.test;

    if (props.result)
      return {
        errors: props.result.result?.errors || [],
        content: props.result.resource_content,
        name:
          props.result.resource_display_name ||
          props.result.resource.external_id,
      };

    return undefined;
  }, [props.result, props.test]);

  return (
    <WbTabsListContext
      value={selectedTab}
      onValueChange={value => setSelectedTab(value as Tab)}
    >
      <WbCard
        cardStyle={{ height: '100%' }}
        title="Overview"
        actions={
          <WbTabsList aria-label="Overview sections">
            <WbTabsTrigger value={Tab.Result} title="Result" />
            <WbTabsTrigger value={Tab.Entity} title="Policy" />
          </WbTabsList>
        }
      >
        <WbCardContent>
          <WbTabsContent value={Tab.Result}>
            <Box
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              {props.infoContent ? (
                <WbCard title="Info">
                  <WbCardContent>{props.infoContent}</WbCardContent>
                </WbCard>
              ) : (
                props.result && (
                  <GovernanceTestOverviewInfo result={props.result} />
                )
              )}
              {test && (
                <GovernanceTestOverviewPolicyDetails
                  policy={props.policy}
                  test={test}
                />
              )}
            </Box>
          </WbTabsContent>
          <WbTabsContent value={Tab.Entity}>
            <GovernanceTestOverviewPolicyInfo {...props} />

            {props.policy.engine === Engine.Cue && (
              <Box mt={2}>
                <PolicyCueScriptEditor
                  cueScript={props.policy.cueSpecific.cueScript}
                  editable={false}
                />
              </Box>
            )}
            {props.policy.engine === Engine.GovernanceAgent && (
              <Box mt={2}>
                <PolicyWittyGovernanceAgentEditor
                  policyContent={props.policy.governanceAgentSpec.prompt}
                  editable={false}
                />
              </Box>
            )}
          </WbTabsContent>
        </WbCardContent>
      </WbCard>
    </WbTabsListContext>
  );
};
